/* eslint-disable @typescript-eslint/no-explicit-any */
import { TmBaseComponent } from '@treasury/presentation';
import { circleCheckmarkIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-button';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'treasury-pending-approval-template';
@customElement(tagName)
export class TreasuryPendingApprovalTemplate extends TmBaseComponent {
    @property({ type: Array })
    public approvals = [] as any;

    @property()
    public completedApprovalCount?: number;

    @property({ type: Object })
    public approvalItem = {} as any;

    @property({ attribute: false })
    public eligibleApproversFunction?: () => Promise<string[]>;

    @property({ type: Boolean })
    public eligibleApproversVisible = false;

    @state()
    private loading = false;

    @state()
    private eligibleApprovers: string[] = [];

    protected async firstUpdated() {
        this.loading = true;
        if (!this.eligibleApprovers.length && this.eligibleApproversFunction) {
            this.eligibleApprovers = await this.eligibleApproversFunction();
        }
        this.loading = false;
    }

    protected render() {
        return html`
            <div class="bg-primary">
                ${this.renderLoader()}${this.renderApprovalCountStatus()} ${this.renderApprovers()}
                <div class="px-6 py-3">${this.renderEligibleApprovers()}</div>
            </div>
        `;
    }

    private viewEligibleApprovers() {
        this.eligibleApproversVisible = !this.eligibleApproversVisible;
        this.dispatchEvent(
            new CustomEvent('eligible-approvers-visible', { detail: this.eligibleApproversVisible })
        );
    }

    private renderApprovers() {
        if (!this.approvals) {
            return nothing;
        }
        return this.approvals.map(
            (approval: any) =>
                html`<div class="px-6 pt-3">
                    <div class="relative ml-3 py-1 border-b text-sm">
                        <div class="approval-icon">${circleCheckmarkIcon}</div>
                        <div class="text-header">${approval.name}</div>
                        <div class="text-xs text-positive">Approved</div>
                    </div>
                </div>`
        );
    }

    private renderEligibleApproversList() {
        if (!this.eligibleApproversVisible) {
            return nothing;
        }
        const approversList = this.eligibleApprovers.map(
            (approver: any) => html`<div class="py-1 text-sm overflow-hidden">${approver}</div>`
        );
        return html`<div class="mb-2 font-medium text-sm text-center">Eligible Approvers</div>
            <div class="approvers-list mb-2">${approversList}</div>`;
    }

    private renderEligibleApprovers() {
        if (!this.eligibleApprovers?.length) {
            return html`<div class="font-medium text-sm text-center">No Eligible Approvers</div>`;
        }
        const buttonText = this.eligibleApproversVisible ? 'Hide' : 'Eligible Approvers';
        return html`${this.renderEligibleApproversList()}
            <tm-button importance="secondary" @click=${() => this.viewEligibleApprovers()}>
                ${buttonText}
            </tm-button>`;
    }

    private renderApprovalCountStatus() {
        if (!this.approvals) {
            return nothing;
        }
        const approvalPluralize =
            this.approvalItem.numberOfApprovalsNeeded === 1 ? 'approval' : 'approvals';
        const approvalsNeeded = this.approvalItem.numberOfApprovalsNeeded
            ? `of ${this.approvalItem.numberOfApprovalsNeeded}`
            : nothing;
        return html`<div class="border-b px-6 py-3">
            <p class="text-base font-medium text-header">Approval Status</p>
            <div class="text-xs text-secondary">
                ${this.completedApprovalCount ? this.completedApprovalCount : this.approvals.length}
                ${approvalsNeeded} ${approvalPluralize} completed
            </div>
        </div>`;
    }

    private renderLoader() {
        if (!this.loading) {
            return nothing;
        }
        return html`<tm-loader card></tm-loader>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    box-shadow: var(--content-shadow);
                }
                .approvers-list {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
                .approval-icon {
                    position: absolute;
                    left: -28px;
                    top: 50%;
                    transform: translateY(-50%);
                }
                .approval-icon svg path {
                    fill: var(--content-positive);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TreasuryPendingApprovalTemplate;
    }
}
